import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../components/DataTable';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAllProjects } from '../redux/slice/project.slice';
import { getAllCategories } from '../redux/slice/category.slice';
import { deleteProject } from '../redux/slice/project.slice';
import { Link } from 'react-router-dom';
import { Plus, Folder, Layout, CheckCircle, Clock, Search } from 'lucide-react';
import ViewProjectModal from '../components/ViewProjectModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const Projects = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projects, loading, error } = useSelector((state) => state.project);
    const { categories } = useSelector((state) => state.category);

    // Local State
    const [searchTerm, setSearchTerm] = useState('');

    // Modal State
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    useEffect(() => {
        dispatch(getAllProjects());
        dispatch(getAllCategories());
    }, [dispatch]);

    const handleDelete = (id) => {
        setProjectToDelete(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (projectToDelete) {
            dispatch(deleteProject(projectToDelete));
            setDeleteModalOpen(false);
            setProjectToDelete(null);
        }
    };

    const handleEdit = (id) => {
        navigate(`/projects/edit/${id}`);
    };

    const handleView = (id) => {
        const project = projects.find(p => p._id === id || p.id === id);
        if (project) {
            setSelectedProject(project);
            setViewModalOpen(true);
        }
    };

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c._id === catId);
        return cat ? cat.name : 'Unknown';
    };

    // Filter Logic
    const filteredProjects = (projects || []).filter(project => {
        const catName = getCategoryName(project.category).toLowerCase();
        return (
            (project.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (project.sku?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            catName.includes(searchTerm.toLowerCase())
        );
    });

    const columns = [
        {
            header: 'Image',
            accessor: 'images',
            className: 'w-20',
            render: (project) => (
                project.images && project.images.length > 0 ? (
                    <div className="h-12 w-12 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img src={project.images[0].url} alt={project.title} className="h-full w-full object-cover transition-transform hover:scale-110" />
                    </div>
                ) : (
                    <div className="h-12 w-12 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-200">
                        <Layout className="w-5 h-5" />
                    </div>
                )
            )
        },
        {
            header: 'Project Details',
            accessor: 'title',
            render: (project) => (
                <div>
                    <div className="font-semibold text-gray-900 line-clamp-1">{project.title}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider font-medium">{project.sku}</div>
                </div>
            )
        },
        {
            header: 'Category',
            accessor: 'category',
            className: "table-cell",
            render: (project) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    {getCategoryName(project.category)}
                </span>
            )
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (project) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${project.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${project.status === 'Completed' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    {project.status}
                </span>
            )
        }
    ];

    // Stats calculation
    const totalProjects = projects?.length || 0;
    const completedProjects = (projects || []).filter(p => p.status === 'Completed').length;
    const ongoingProjects = (projects || []).filter(p => p.status === 'Ongoing').length;

    if (loading) return (
        <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );

    return (
        <div className="max-w-[95%] mx-auto py-8">
            <div className="space-y-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h2 className="text-3xl font-serif font-bold text-gray-900">Projects</h2>
                        <p className="text-gray-500 mt-1">Manage and track your portfolio projects</p>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard
                        title="Total Projects"
                        value={totalProjects}
                        icon={Folder}
                        color="bg-blue-50 text-blue-600"
                    />
                    <StatCard
                        title="Ongoing Projects"
                        value={ongoingProjects}
                        icon={Clock}
                        color="bg-yellow-50 text-yellow-600"
                    />
                    <StatCard
                        title="Completed Projects"
                        value={completedProjects}
                        icon={CheckCircle}
                        color="bg-green-50 text-green-600"
                    />
                </div>

                {/* Toolbar */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-all"
                        />
                    </div>

                    <Link
                        to="/projects/new"
                        className="flex items-center text-nowrap px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all shadow-md hover:shadow-lg w-full sm:w-auto justify-center"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Project
                    </Link>
                </div>

                {/* Table */}
                <DataTable
                    columns={columns}
                    data={filteredProjects}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onView={handleView}
                    error={error}
                />
            </div>

            {/* View Project Modal */}
            <ViewProjectModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                project={selectedProject}
                categories={categories}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Project"
                message="Are you sure you want to delete this project? This action cannot be undone."
            />
        </div>
    );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between transition-transform hover:scale-[1.02]">
        <div>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-4 rounded-xl ${color}`}>
            <Icon className="w-6 h-6" />
        </div>
    </div>
);

export default Projects;
